import { UzCombobox } from '@utazzo/ui';

import { usePickerOptions } from './usePickerOptions';

import * as citiesApi from '@/features/cities/cities.api';

interface IataCityPickerProps {
  label?: string;
  /** Selected IATA code (e.g. "DEL"). */
  value: string | null;
  onValueChange: (iata: string | null) => void;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
}

/**
 * City picker that stores the city's IATA airport code (not its uuid) - for
 * flight search where the origin/destination is an IATA code. Only cities that
 * carry an `iataCode` are shown; the option label is "City (CODE)".
 */
export const IataCityPicker = ({
  label = 'City',
  value,
  onValueChange,
  disabled,
  error,
  placeholder = 'Search city or airport',
}: IataCityPickerProps): React.JSX.Element => {
  const { options, loading, setSearch } = usePickerOptions(
    {
      cacheKey: 'picker.iata-cities',
      fetch: async (search) => {
        const res = await citiesApi.listCities({
          page: 1,
          pageSize: 30,
          sortDir: 'asc',
          isActive: true,
          ...(search ? { search, searchMode: 'prefix' as const } : {}),
        });
        return res.data.filter((c) => Boolean(c.iataCode));
      },
      // The selected value is an IATA code, so resolve it by searching for it.
      // `prefix` search matches city NAME too, so a common 3-letter prefix (e.g.
      // "DEL" -> every "Del..." city) can bury the exact-IATA city (Delhi) past a
      // small page window and leave the box blank. Page wide enough that the
      // exact match is always present, then pick it by exact code.
      fetchOne: async (iata) => {
        const res = await citiesApi.listCities({
          page: 1,
          pageSize: 100,
          sortDir: 'asc',
          isActive: true,
          search: iata,
          searchMode: 'prefix',
        });
        return (
          res.data.find((c) => (c.iataCode ?? '').toUpperCase() === iata.toUpperCase()) ?? null
        );
      },
      toOption: (c) => ({
        value: c.iataCode as string,
        label: `${c.name} (${c.iataCode})`,
        description: c.country?.name ?? undefined,
      }),
    },
    value,
  );

  return (
    <UzCombobox
      label={label}
      placeholder={placeholder}
      searchPlaceholder="Search city or IATA..."
      value={value ?? ''}
      onValueChange={(v) => onValueChange(v || null)}
      onSearchChange={setSearch}
      options={options}
      loading={loading}
      disabled={disabled}
      error={error}
    />
  );
};
